/**
 * @file components/common/AuditorSelect.tsx
 * @description 共用「稽核員」下拉選擇器（從員工檔案搜尋勾選，選中存 userId）。
 *
 * 用法（主鍵存 auditorId）：
 * ```tsx
 * <Form.Item label="稽核員" name="auditorId" rules={[{ required: true, message: '請選擇稽核員' }]}>
 *   <AuditorSelect />
 * </Form.Item>
 * ```
 * - 主字段 value = userId (number)
 * - 顯示員工姓名，支援輸入姓名搜尋。
 *
 * 數據來源：useAuditorOptions hook（共享 TanStack Query 快取）。
 */

import { useMemo } from 'react';
import { Select, type SelectProps } from 'antd';
import { useAuditorOptions, type AuditorOption } from '@/hooks/useAuditorOptions';

/** AuditorSelect Props（在 antd Select 基礎上擴充） */
export type AuditorSelectProps = Omit<
  SelectProps,
  'options' | 'loading' | 'showSearch' | 'filterOption' | 'mode'
>;

/**
 * 稽核員下拉選擇器。
 */
export default function AuditorSelect({
  placeholder = '請搜尋並選擇稽核員',
  ...rest
}: AuditorSelectProps) {
  const { data, isLoading } = useAuditorOptions();

  /** 稽核員候選名單 */
  const auditors: AuditorOption[] = useMemo(() => data?.items ?? [], [data]);

  /** 轉為 Select options，value = userId，label = 姓名 */
  const options = useMemo(
    () =>
      auditors.map((a) => ({
        value: a.id,
        label: a.username ? `${a.name}（${a.username}）` : a.name,
      })),
    [auditors],
  );

  return (
    <Select
      placeholder={placeholder}
      showSearch
      loading={isLoading}
      optionFilterProp="label"
      filterOption={(input, option) =>
        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
      }
      options={options}
      {...rest}
    />
  );
}
